import React, { useCallback, useMemo, useState } from 'react';
import {
  Image,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { applyTransparency } from '../constants/colors';
import { RootStackParamList } from './RootNavigator';
import { Route, RouteProp, useNavigation } from '@react-navigation/native';
import { useSignedInContext } from '../context/SignedInContext';
import { useGetHivesQuery } from '../components/screens/MyHives/useGetHivesQuery.gql';
import { Linking } from 'react-native';
import { useSeenNotification } from '../context/NotificationContext';
import { set } from 'lodash';

const diamondImage = require('../resources/images/diamond.png');

type HeaderProps = {
  route: Route<string>;
};

const Header = ({ route }: HeaderProps) => {
  const { user: currentUser, isSignedIn } = useSignedInContext();
  const { data: getHivesData } = useGetHivesQuery({ skip: !isSignedIn });
  const { width } = useWindowDimensions();
  const { setSeenStatus, seenStatus } = useSeenNotification();

  const shouldHideBanner = useMemo(() => {
    const isLobbyPage = (route as RouteProp<RootStackParamList>).name === 'Lobby';

    // ✅ Show only on  web (i.e. browser, not app)
    const isWeb = Platform.OS === 'web';

    if (!isLobbyPage || !isWeb) {
      return true; // 🔒 Hide in all other cases
    }

    return false; // ✅ Show only on mobile web, on the Lobby page
  }, [currentUser?.userType, getHivesData, isSignedIn, route, width]);

  const onLearnMore = useCallback(() => {
    setSeenStatus(true);
    Linking.openURL('https://www.numberhive.app/offers/v4-launch/');
  }, []);

  if (shouldHideBanner || seenStatus) {
    return null;
  }

  return (
    <View
      style={[
        styles.header,
        { marginLeft: (width - 1000) / 2 + 15 },
        width <= 1250 && styles.stickHeader,
      ]}
    >
      <View style={[styles.banner, width <= 1250 && styles.stickBanner]}>
        <LinearGradient
          style={[styles.gradienContainer, width <= 768 && styles.mobileGradient]}
          colors={['#747474', '#1F1F1F']}
        >
          {width > 1250 ? (
            // Desktop layout
            <>
              <Image resizeMode="cover" style={styles.diamondImage} source={diamondImage} />
              <Text style={styles.highlightedText}>Version 4 is here!</Text>
              <Text style={styles.bannerText}>
                Experience the new and improved way to build confident learners. Sign up now to
                activate your account.
              </Text>
              <View style={styles.closeButtonContainer}>
                <TouchableOpacity onPress={onLearnMore}>
                  <LinearGradient style={styles.button} colors={['#FFCF53', '#FF9900']}>
                    <Text maxFontSizeMultiplier={1} style={styles.learnMoreText}>
                      Learn more
                    </Text>
                  </LinearGradient>
                </TouchableOpacity>
                <TouchableOpacity style={styles.buttonX} onPress={() => setSeenStatus(true)}>
                  <Text style={styles.textX}>✕</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : (
            // Mobile layout
            <View style={styles.mobileTextContainer}>
              <View style={styles.mobileBannerTextContainer}>
                <View style={styles.mobileHeaderTextContainer}>
                  <Image
                    resizeMode="cover"
                    style={styles.diamondImageMobile}
                    source={diamondImage}
                  />
                  <Text style={styles.highlightedTextMobile}>Version 4 is here!</Text>
                </View>
                <View style={styles.closeButtonContainer}>
                  <TouchableOpacity onPress={onLearnMore}>
                    <LinearGradient style={styles.button} colors={['#FFCF53', '#FF9900']}>
                      <Text maxFontSizeMultiplier={1} style={styles.learnMoreText}>
                        Learn more
                      </Text>
                    </LinearGradient>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.buttonX} onPress={() => setSeenStatus(true)}>
                    <Text style={styles.textX}>✕</Text>
                  </TouchableOpacity>
                </View>
              </View>
              <Text style={styles.bannerTextMobile}>
                Experience the new and improved way to build confident learners. Sign up now to
                activate your account.
              </Text>
            </View>
          )}
        </LinearGradient>
      </View>
    </View>
  );
};

export { Header };

const styles = StyleSheet.create({
  header: {
    position: 'absolute',
    alignItems: 'center',
    marginTop: 15,
  },
  stickHeader: {
    position: 'relative',
    marginTop: 0,
    alignItems: 'stretch',
    marginLeft: 0,
  },
  banner: {
    borderRadius: 50,
    borderWidth: 4,
    borderColor: '#9C9C9C',
    overflow: 'hidden',
  },
  stickBanner: {
    borderRadius: 0,
    borderWidth: 0,
  },
  gradienContainer: {
    paddingHorizontal: 20,
    paddingVertical: 11,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bannerText: {
    fontFamily: 'Secular One',
    fontSize: 15,
    color: '#FFFFFF',
    fontWeight: '800',
    marginEnd: 14,
  },
  highlightedText: {
    fontWeight: '400',
    fontFamily: 'Secular One',
    fontSize: 24,
    color: '#FFFFFF',
    marginRight: 16,
    lineHeight: 22,
  },
  highlightedTextMobile: {
    fontWeight: '800',
    fontFamily: 'Secular One',
    fontSize: 18,
    color: '#FFFFFF',
    marginRight: 16,
  },
  diamondImage: {
    width: 45,
    height: 45,
    marginEnd: 14,
  },
  diamondImageMobile: {
    width: 32,
    height: 32,
    marginEnd: 14,
  },
  button: {
    borderRadius: 50,
    paddingHorizontal: 18,
    paddingVertical: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  learnMoreText: {
    fontFamily: 'Secular One',
    fontSize: 15,
    color: '#453B3B',
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 3 },
    textShadowRadius: 2,
  },
  learnMoreTextMobile: {
    fontFamily: 'Secular One',
    fontSize: 12,
    color: '#453B3B',
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 3 },
    textShadowRadius: 2,
  },
  mobileTextContainer: {
    flex: 1,
    flexDirection: 'column',
    marginEnd: 14,
  },

  bannerTextMobile: {
    fontFamily: 'Secular One',
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
    marginTop: 4,
  },

  mobileGradient: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  mobileBannerTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  mobileHeaderTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  buttonX: {
    backgroundColor: '#999999', // Gray background
    width: 40,
    height: 40,
    borderRadius: 20, // Makes it circular
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 10,
  },
  textX: {
    color: '#000', // Black 'X'
    fontSize: 20,
    fontWeight: 'bold',
  },
  closeButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
});
